import { IAddress, IPharmacyAddress } from './common.dto';
import { IBigcommerceOrder } from './bigcommerce/bigcommerce-order';
import { IBigcommerceOrderShipment } from './bigcommerce/bigcommerce-shipment';
import {
  ICartLineItem,
  ICustomCartLineItem,
  IProductItem
} from './product.dto';
import { IInvoiceItem } from './invoice';
import { ICustomerUser } from './customer.dto';
import { IAdminUser } from './crm-user';
import { IPharmacy } from './pharmacy';
import type Stripe from 'stripe';
import { IConsultation } from './consultation.dto';
import { IProductRequirement, IProductV2Extras } from './productV2.dto';
import { ILineItemDiscount } from './cart.dto';
import { IProductCategory } from './product-category.dto';
import { Types } from 'mongoose';
import type {
  PaymentProcessorId,
  PaymentProcessorImplementationId
} from './payment/gateway';
import type { IGiftCardRestrictions } from './gift-card';
import { RequirementType } from './settings.dto';
// import { INSURANCE_TYPES } from '@care-portals/data';
// Local type definition to avoid circular dependency

export const PHARMACY_ORDER_ID_KEYS = [
  'curexaId', // referenceId for Curexa order
  'digitalRxOrderId', // DigitalRx QueueID / order identifier saved on the EMR order
  'gogomedsId', // referenceId for GogoMeds order
  'goGoMedsReferenceId', // GoGoMeds AffiliateOrderNumber from API response (e.g. SVS-prefixed reference)
  'junctionOrderId', // Junction order ID
  'lifeFileId', // referenceId for LifeFile order
  'pharmacyId', // Beluga pharmacy identifier assigned to the EMR order
  'pharmacyHubId', // referenceId for PharmacyHub order
  'pharmacyHubTransactionId', // transactionId for PharmacyHub order
  'pharmetikaOrderId', // referenceId for Pharmetika order
  'pocketpillsOrderId', // PocketPills order identifier stored on the EMR order
  'pocketpillsPrescriptionIds', // PocketPills prescription identifiers stored on the EMR order (comma-separated)
  'wellsyncOrderId', // WellSync order identifier stored on the EMR order
  'wellsyncExternalId', // WellSync external case identifier stored on the EMR order
  'belugaPharmacyId', // Beluga-side pharmacy identifier set by the beluga webhook
  'rxVortexOrderId', // RxVortex order tracking identifier
  'internalReferenceId', // RxVortex internal reference identifier (mirrors CarePortals order id)
  'precisionOrderId', // Precision Pharmacy order identifier stored on the EMR order
  'bondRxOrderId', // BondRx order identifier stored on the EMR order
  'pharmacyOrderId', // Standardized pharmacy order UUID (pair with pharmacySource)
] as const;

export type PharmacyOrderIds = {
  [K in (typeof PHARMACY_ORDER_ID_KEYS)[number]]?: string;
} & {
  /**
   * @description Junction error message stored on the EMR order
   */
  junctionError?: string;
};

export type GoGoMedsOrderExtras = {
  /**
   * @description GoGoMeds API order id (numeric OrderId from SubmitOrder / webhooks)
   */
  goGoMedsOrderId?: number;
  /**
   * @description GoGoMeds AffiliateOrderNumber from API response (e.g. SVS-prefixed reference)
   */
  goGoMedsReferenceId?: string;
};

export type WellSyncOrderExtras = {
  /**
   * @description WellSync order ID
   */
  wellsyncExternalId?: string;
  /**
   * @description WellSync order ID
   */
  wellsyncOrderId?: string;
  /**
   * @description WellSync error message
   */
  wellsyncError?: string;
  /**
   * @description WellSync failed prescription items
   */
  wellsyncFailedPrescriptionItems?: string;
};

export type PharmetikaV2OrderExtras = {
  /**
   * @description PharmetikaV2 medication order identifier
   */
  pharmetikaOrderId?: string;

  /**
   * @description PharmetikaV2 error message
   */
  pharmetikaError?: string;
};

export type PrecisionOrderExtras = {
  /**
   * @description Precision order ID
   */
  precisionOrderId?: string;
  /**
   * @description Precision error message
   */
  precisionError?: string;
};

export type PaymentStatusKey =
  | 'skipped'
  | 'paid'
  | 'failed'
  | 'pending'
  | 'refunded'
  | 'voided'
  | 'processing'
  | 'authorized'
  | 'requires_action'
  | 'partially_refunded'
  | 'delayed_capture'
  | 'partial_paid'
  | 'adjudicated';

/**
 * @description Payment statuses that mean money has already been collected for
 * the order. `partially_refunded` belongs here: a partial refund only returns
 * some of a payment that did succeed, so the order must never be charged
 * again. `refunded` is deliberately excluded - a fully refunded order is
 * re-chargeable (see `needsFreshCharge`), as is `partial_paid`, where only part
 * of the balance has been collected. `needsFreshCharge` stays limited to those
 * re-chargeable statuses so a succeeded intent on a settled order is still
 * treated as already paid.
 */
export const SETTLED_PAYMENT_STATUSES: readonly PaymentStatusKey[] = [
  'paid',
  'partially_refunded'
];

export const isSettledPaymentStatus = (
  status?: PaymentStatusKey | null
): boolean => !!status && SETTLED_PAYMENT_STATUSES.includes(status);

export class NewOrderDTO {
  cartId: string;
  paymentMethodId: string;
  shippingAddress: IAddress;
  isNewShippingAddress: boolean;
  couponCode?: string;
  pauseCollection?: boolean;
  extras?: any;
  assignedToGroup?: string;
  dueDate?: string;
}

export class PayInvoiceWithPaymentMethodDTO {
  invoiceId: string;
  paymentMethodId: string;
}

export type OrderSource =
  | 'checkout'
  | 'subscription_cycle'
  | 'manual'
  | 'manual_refill'
  | 'prescription';

export type OrderDirection = 'next' | 'previous';

export interface IProviderPrescribingContextItem {
  label: string;
  value?: string;
  hint?: string;
}

/** `items` = rows; `indicators` = tags. New entries in those arrays render without EMR changes. */
export interface IProviderPrescribingContext {
  version?: number;
  title?: string;
  items?: IProviderPrescribingContextItem[];
  indicators?: string[];
}

export type BelugaOrderExtras = {
  belugaPharmacyId?: string;
  belugaPharmacyOrderStatus?: string;
  masterId?: string;
  visitId?: string;
  belugaScheduleLink?: string;
  prescriberNetwork?: string;
  /**
   * Prior Beluga patientPreference meds for this order's visit.
   * Kept so Update Visit can switch back to meds that are no longer current
   * and may not be in Beluga dashboard favorites.
   */
  belugaVisitMedHistory?: Array<{
    medId: string;
    name: string;
    strength?: string;
    quantity?: string;
    refills?: string;
    savedAt?: string;
  }>;
  /** EasyPost / Beluga package tracker status from PACKAGE_* webhooks. */
  trackerStatus?: string;
  /** EasyPost / Beluga package tracker ID from PACKAGE_* webhooks. */
  trackerId?: string;
  /** Delivery timestamp string from PACKAGE_* webhooks (when provided). */
  deliveredDate?: string;
  /** Set when Beluga reports PACKAGE_DELIVERY_FAILED; cleared on later successful package updates. */
  belugaPackageError?: string;
};

export type BondApiOrderExtras = {
  /** BondRx-assigned order identifier returned after submission. */
  bondRxEncounterId?: string;
  /** BondRx-assigned order identifier returned after submission. */
  bondRxOrderId?: string;
  /** Last BondRx error message captured during adapter/middleware processing. */
  bondRxError?: string;
  /** Latest BondRx fulfillment status (e.g. `PATIENT WAITING`, `SHIPPED`). */
  bondRxStatus?: string;
};

/**
 * Which pharmacy owns the standardized `pharmacyOrderId`.
 *
 * Declared once for every integration that writes it: per-pharmacy extras types are intersected
 * into {@link OrderExtras}, so two of them narrowing this to different literals would collapse the
 * field to `never`. Each integration re-narrows it in its own adapter types instead.
 */
export type PharmacyOrderSource = 'blend' | 'optiorx' | 'tmc';

/** One pharmacy-side order per product, listed under the drug's DIN in {@link PharmacyDrugOrderExtras.pharmacyOrders}. */
export type PharmacyDrugOrder = {
  /** Pharmacy-assigned identifier for this product's order (OptioRx: acknowledgementId). */
  pharmacyOrderId: string;
  /** The pharmacy's catalogue code for the product submitted (OptioRx: `item_alternate_id`). */
  itemId?: string;
  /** Product label as submitted to the pharmacy (OptioRx: `item`). */
  itemName?: string;
  /** Latest pharmacy prescription status, verbatim (OptioRx: `rxStatusTypeText`). */
  status?: string;
  /** Latest pharmacy transaction status, verbatim (OptioRx: `rxTransactionStatusTypeText`). */
  transactionStatus?: string;
  /** Pharmacy-generated Rx number, once assigned. */
  rxNumber?: string;
  trackingNumber?: string;
  /** Shipping carrier as the pharmacy names it, e.g. `FedEx`, `UPS`. */
  carrier?: string;
  shippedAt?: string;
  deliveredAt?: string;
  /**
   * Identifier of the last webhook applied to this entry, for idempotency:
   * pharmacies retry deliveries, so the same event can arrive more than once.
   */
  lastEventId?: string;
};

export type PharmacySourceExtras = {
  pharmacySource?: PharmacyOrderSource;
  /**
   * Last error from whichever pharmacy owns `pharmacySource`. Pairs with it, so a reader never has
   * to know which integration wrote the order to surface its failure.
   */
  pharmacyError?: string;
};

/**
 * Per-drug pharmacy fulfillment, for pharmacies that fill one prescription as
 * several orders. Separate from {@link PharmacySourceExtras} because most
 * integrations submit a single order and never write these.
 */
export type PharmacyDrugOrderExtras = {
  /**
   * Flat list of every pharmacy order id on this order.
   *
   * Redundant with the ids inside `pharmacyOrders`, and deliberately so: webhook
   * correlation looks an order up *by* a pharmacy order id, which a keyed object
   * cannot serve without a scan. Indexed on the orders collection.
   */
  pharmacyOrderIds?: string[];
  /** Keyed by DIN; one entry per product, in `extras.optiorx` order for OptioRx. */
  pharmacyOrders?: Record<string, PharmacyDrugOrder[]>;
};

export type BlendOrderExtras = {
  /** Blend human-readable order number (e.g. `ORD250312003548IAU4`). */
  blendOrderNumber?: string;
  /** Blend patient UUID created/referenced for this order. */
  pharmacyPatientId?: string;
  /** Blend prescriber UUID created/referenced for this order. */
  pharmacyPrescriberId?: string;
  /** Blend electronic prescription UUID created for this order. */
  pharmacyEPrescriptionId?: string;
  /**
   * Set on `order.replaced`: the Blend order this one superseded. `pharmacyOrderId` moves on to the
   * replacement so later events still correlate.
   */
  blendReplacedOrderId?: string;
  /** Latest Blend order status (e.g. `received`, `picked`, `shipped`). */
  blendStatus?: string;
};

export type VitaboomOrderExtras = {
  /** Vitaboom order id (UUID) — the handle for fetch, update and cancel. */
  vitaboomOrderId?: string;
  /** Shopify order name as shown to the customer, e.g. `#1001`. */
  vitaboomOrderName?: string;
  /** Latest Vitaboom fulfillment status (e.g. `unfulfilled`, `fulfilled`). */
  vitaboomStatus?: string;
  /** Last Vitaboom error captured during adapter/middleware processing. */
  vitaboomError?: string;
  /**
   * `eventId` of the last Vitaboom webhook applied to this order. Vitaboom retries
   * up to 3 times, so the same event can arrive more than once; this makes the
   * handler idempotent.
   */
  vitaboomLastEventId?: string;
};

export type TmcOrderExtras = {
  /** Latest TMC `order_status` (e.g. `Order Received`, `Cancelled`). */
  tmcStatus?: string;
  /** Latest TMC `shipping_status` (e.g. `Pending`, `Cancelled`). */
  tmcShippingStatus?: string;
  /** TMC/Pharmetika opportunity id echoed back by `RetrieveOrderStatus`. */
  tmcOpportunityId?: string;
  /** TMC/Pharmetika opportunity name, e.g. `Doe-074027`. */
  tmcOpportunityName?: string;
};

export type OrderExtras = BelugaOrderExtras &
  VitaboomOrderExtras &
  PharmacyOrderIds &
  PharmacySourceExtras &
  PharmacyDrugOrderExtras &
  GoGoMedsOrderExtras &
  WellSyncOrderExtras &
  BondApiOrderExtras &
  BlendOrderExtras &
  TmcOrderExtras &
  PharmetikaV2OrderExtras &
  PrecisionOrderExtras & {
    /**
     * @description status from Curexa
     */
    curexaStatus?: string;
    /**
     * @description If the order has isStarter product, and the customer wants to still get prescribed a starter, then this is set to true
     */
    isStarterOverride?: string;

    /**
     * @description GoGoMeds API order id (numeric OrderId from SubmitOrder / webhooks)
     */
    gogomedsOrderId?: number;
    /**
     * @description GoGoMeds AffiliateOrderNumber from API response (e.g. SVS-prefixed reference)
     */
    goGoMedsReferenceId?: string;

    [x: string]: any;
  };

/**
 * @description Status of the order in the laboratory
 *  - ordered: Laboratory received the order, stored it into their system, and started processing it asynchronously.
 *  - requisition_created: An order requisition form was validated and created with the partner laboratory, making the order available to be carried out.
 *  - requisition_bypassed: An order requisition form wasn't created when the order was placed with us because it already existed.
 *  - transit_customer: The testkit is shipped and in transit to the customer.
 *  - out_for_delivery: The testkit is out for delivery.
 *  - with_customer: The testkit is delivered to the customer.
 *  - transit_lab: The customer has sent the testkit back to the lab.
 *  - delivered_to_lab: The lab has received the testkit.
 *  - failure_to_deliver_to_customer: The shipping company was unable to deliver the testkit to the customer.
 *  - failure_to_deliver_to_lab: The shipping company was unable to deliver the testkit to the lab.
 *  - problem_in_transit_customer: The shipping company encountered a problem while delivering the testkit to the customer.
 *  - problem_in_transit_lab: The shipping company encountered a problem while delivering the testkit to the lab.
 *  - sample_error: The collected sample was unprocessable by the lab.
 *  - completed: The laboratory processed the blood sample and final results are available.
 *  - cancelled: The order was cancelled by either the patient, Laboratory or you.
 */
export type OrderLabStatus =
  | 'ordered'
  | 'requisition_created'
  | 'requisition_bypassed'
  | 'transit_customer'
  | 'out_for_delivery'
  | 'with_customer'
  | 'transit_lab'
  | 'delivered_to_lab'
  | 'failure_to_deliver_to_customer'
  | 'failure_to_deliver_to_lab'
  | 'problem_in_transit_customer'
  | 'problem_in_transit_lab'
  | 'sample_error'
  | 'completed'
  | 'cancelled';

export interface IOrderLab {
  status?: OrderLabStatus;
  shipmentToPatient?: {
    carrier?: string;
    trackingCode?: string;
    trackingLink?: string;
  };
  shipmentToLab?: {
    carrier?: string;
    trackingCode?: string;
    trackingLink?: string;
  };
  bookingLink?: string;
}

export class IOrder {
  organization: string;
  _id: string;
  id: number;
  createdAt?: Date;
  updatedAt?: Date;
  shippedAt?: Date;
  deliveredAt?: Date;
  customer: string | Partial<ICustomerUser>;
  status: OrderStatusKey;
  statusHistory: OrderStatusHistory[];
  extras?: OrderExtras;
  meta?: any;
  source: OrderSource;
  lineItems?: IOrderLineItem[];
  invoices?: Stripe.Invoice[];
  shippingAddress?: IAddress;
  pharmacyAddress?: IPharmacyAddress;
  coupon?: string;
  totalAmount: number; // after discount + incl. taxes
  discountAmount: number;
  baseAmount: number; // before discount
  subTotalAmount: number; // before discount and other fees
  note?: string;
  assignedTo?: string | IAdminUser;
  assignedPharmacy?: string | IPharmacy;
  assignedToGroup?: string;
  trackingCode?: string;
  carrier?: string;
  trackingLink?: string;
  requirements?: IOrderRequirement[];
  version?: number;
  isDeleted?: boolean;
  creditUsedAmount?: number;
  postPurchaseCredit?: number;
  payment?: IOrderPayment;
  totalInsuranceAmount?: number;
  isFirstOrder?: boolean;
  transferOut?: boolean;
  createdBy: string;
  giftCardCode?: string; // gift card code used for payment
  giftCardUsedAmount?: number; // amount paid using gift card
  pharmacyOrder?: IPharmacyOrder;
  lab?: IOrderLab;
}

export interface CheckoutOrderRequirementsChecks {
  hasUnsatisfiedQnrRequirements: boolean;
  hasUnsatisfiedVerificationRequirements: boolean;
}

export class OrderStatusHistory {
  prevStatus: OrderStatusKey;
  newStatus: OrderStatusKey;
  updatedAt: Date;
  userId: string;
}

export class IOrderLineItem {
  name: string;
  subLabel?: string;
  productId: string | number | Types.ObjectId;
  variantId?: number | string;
  sku?: string;
  listPrice: number; // original price before discounts (as set in product catalog)
  salePrice: number; // sale price as set in product catalog
  price: number; // final price after discounts (coupons/promotions/listprice-salePrice)
  drugPrice?: number; // price of the drug (as set in Kroll)
  visitFee?: number; // fee for the visit
  shippingFee?: number; // fee for the shipping
  insuranceCoverages?: Array<{
    type: any; // (typeof INSURANCE_TYPES)[number]['key']; // TODO: fix this, should not import from lib/data
    amount: number;
  }>; // amount covered by insurance for lineItem (not per unit)
  totalInsuranceAmount?: number; // total amount covered by insurance for lineItem (not per unit)
  optionId?: number | string;
  optionType?: number;
  optionLabel?: string;
  quantity: number;
  imageUrl?: string;
  customer_fields?: any; // from bigC
  requirements?: IProductRequirement[];
  extras?: IProductV2Extras;
  discount?: ILineItemDiscount;
  categories?: (IProductCategory | Types.ObjectId)[];
  isSubscription?: boolean;
  type?: string; // 'physical' | 'digital'
  options?: { [type: string]: string };
  delayedCaptureAmount?: number;
  isRefunded?: boolean;
  refundedQuantity?: number;
  refundedAmount?: number;
  vendor?: string;
  /**
   * Snapshot of the product's `isNonPrescription` flag at the time the order was built.
   * Read (never re-derived from the catalog) so that flipping a product's flag later
   * cannot retroactively change how an in-flight order routes. Absent on orders created
   * before the flag existed, which correctly reads as "prescription required".
   */
  isNonPrescription?: boolean;
  giftCardValue?: number; // gift card value if this line item is a gift card product
  giftCardRestrictions?: IGiftCardRestrictions; // restrictions for the gift card
  providerPrescribingContext?: IProviderPrescribingContext;
}

// checkout order request
export class ICreateOrderRequest {
  organization: string;
  id: number;
  shippedAt?: Date;
  customer: string | Partial<ICustomerUser>;
  status: OrderStatusKey;
  extras?: any;
  meta?: any;
  source: 'checkout' | 'subscription_cycle' | 'manual';
  items?: IInvoiceItem[];
  lineItems?: (ICartLineItem | ICustomCartLineItem)[];
  invoices?: any[];
  shippingAddress?: IAddress;
  coupon?: string;
  totalAmount: number; // after discount + incl. taxes
  discountAmount: number;
  baseAmount: number; // before discount
  subTotalAmount: number; // before discount and other fees
  assignedTo?: string | IAdminUser;
  assignedPharmacy?: string | IPharmacy;
}

export class IOrderStripe {
  payment_method: any;
  error: any;
  invoice: any;
}
/** @deprecated use IUpdateOrderStatusDTO instead */
export class IOrderDTO {
  status_id?: number;
  customer_id?: number;
  [key: string]: any;
}

export class IUpdateOrderStatusDTO {
  status: OrderStatusKey;
  customer_id: string;
}

// tslint:disable-next-line:no-empty-class
/** @deprecated use IOrder instead */
export interface IOrderDetailsDTO extends IBigcommerceOrder {
  shipments?: IBigcommerceOrderShipment[];
  lineItems?: IProductItem[];
  stripe?: IOrderStripe;
}

export class ApplyInvoiceDiscountDTO {
  discountAmount: number;
  stripeId?: string;
  invoiceId?: string;
  description?: string;
}

//May need to update these
export type OrderStatusKey =
  | 'incomplete'
  | 'pending'
  | 'shipped'
  | 'partially_shipped'
  | 'refunded'
  | 'cancelled'
  | 'declined'
  | 'awaiting_payment'
  | 'awaiting_pickup'
  | 'awaiting_shipment'
  | 'completed'
  | 'processing'
  | 'manual_verification_required'
  | 'disputed'
  | 'partially_refunded'
  | 'awaiting_requirements'
  | 'awaiting_script'
  | 'awaiting_fulfillment'
  | 'awaiting_requisition'
  | 'awaiting_results'
  | 'returned'
  | 'rejected';

export type OrderStatus = {
  name: string;
  statsName: string;
  icon: string;
  show: boolean;
  id: number;
  sort: number;
  stage?:
    | 'placement'
    | 'authorization'
    | 'fulfillment'
    | 'delivery'
    | 'closure';
};

export class IOrderShipment {
  /**
   * Shipment ID.
   */
  id: number;
  /**
   * ID of the order associated with this shipment.
   */
  order_id: number;
  /**
   * ID of this order's customer.
   */
  customer_id: number;
  /**
   * ID of the desired `shipping_address` associated with the shipment.
   */
  order_address_id: number;
  /**
   * Creation date for the shipment.
   */
  date_created: string;
  /**
   * Tracking number of the shipment.
   */
  tracking_number: string;
  /**
   * Additional information to describe the method of shipment (ex. Standard, Ship by Weight, Custom Shipment). Can be used for live quotes from certain shipping providers.\nIf different from `shipping_provider`, `shipping_method` should correspond to `tracking_carrier`.
   */
  shipping_method: string;
  /**
   * Enum of the BigCommerce shipping-carrier integration/module.
   */
  shipping_provider:
    | 'auspost'
    | 'canadapost'
    | 'endicia'
    | 'usps'
    | 'fedex'
    | 'ups'
    | 'upsready'
    | 'upsonline'
    | 'shipperhq'
    | ' ';
  /**
   * Tracking carrier for the shipment.\nAcceptable values include an empty string (`""`) or one of the valid tracking-carrier values viewable [here](https://docs.google.com/spreadsheets/d/1w9c_aECSCGyf-oOrvGeUniDl-ARGKemfZl0qSsav8D4/pubhtml?gid=0&single=true) and downloadable as a .CSV file [here](https://docs.google.com/spreadsheets/d/1mTueEynfcEmwsU2y2Jd2MX-8GKwNZrmlRMBcIElg9aY/pub?gid=0&single=true&output=csv).
   */
  tracking_carrier: string;
  /**
   * Returns a tracking link from the shipping service.
   */
  tracking_link: string;
  /**
   * Comments the shipper wishes to add.
   */
  comments: string;
  /**
   * Billing Address
   */
  billing_address: IOrderShipmentAddress;
  /**
   * ID of this order's customer.
   */
  shipping_address: IOrderShipmentAddress;
  /**
   * The items in the shipment. This object has the following members, all integer: order_product_id (required), quantity (required), product_id (read-only). A sample items value might be: [ {"order_product_id":16,"product_id": 0,"quantity":2} ].
   */
  items: Array<{
    order_product_id: number;
    product_id: number;
    quantity: number;
  }>;
}

export class IOrderShipmentAddress {
  first_name?: string;
  last_name?: string;
  company?: string;
  street_1?: string;
  street_2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  country_iso2?: string;
  phone?: string;
  email?: string;
  shipping_method?: string;
  [k: string]: any;
}

// interface IOrderProductResource {
//   url?: string;
//   resource?: string;
//   data?: IProductItem[];
//   [k: string]: any;
// }

// interface IOrderShippingAddressResource {
//   /**
//    * URL of the shipping address for api requests
//    */
//   url?: string;
//   resource?: string;
//   [k: string]: any;
//   data: IOrderShipmentAddress[];
// }

export class IOrderRequirement {
  type: RequirementType;
  status: 'not_satisfied' | 'satisfied';
  qnr?: IConsultation;
  key: string;
  description?: string;
  label: string;
  source?: 'product' | 'manual';
  extras?: any;
}

export class ExportOrdersQuery {
  dateFrom?: string;
  dateTo?: string;
}

/** @deprecated use IGetOrdersDTO for v2; this function still depends on bigCommerce primarily only needed for fetching archived orders (endor potentially still using it) */
export class IGetOrdersQuery {
  status?: string; //from both mongo and bigC
  assignedTo?: string; // from mongo
  assignedPharmacy?: string; // from mongo (assigned pharmacy)
  assignedToGroup?: any; // from mongo
  provinceCode?: string; // from mongo
  customerId?: string;
  customerEmail?: string;
  customerFirstName?: string;
  customerLastName?: string;
  minCreatedDate?: string; // from bigC
  maxCreatedDate?: string; // from bigC
  minUpdatedDate?: string;
  maxUpdatedDate?: string;
  minOrderId?: number; // from bigC
  maxOrderId?: number; // from bigC
  limit?: number;
  'status:in'?: string; // from mongo
  includeArchived?: boolean;
  includeCount?: boolean;
  source?: OrderSource;
  group?: string[];
  sortField?: string;
  sortOrder?: 1 | -1;
  pharmetikaOrderId?: string;
  paymentStatus?: string | string[];
  curexaId?: string;
}

export class IGetOrdersDTO<TExtras = OrderExtras, TMeta = Record<string, any>> {
  status?: string | string[]; //from both mongo and bigC
  assignedTo?: string | string[]; // from mongo (assigned user)
  assignedPharmacy?: string | string[]; // from mongo (assigned pharmacy)
  assignedToGroup?: any; // from mongo
  provinceCode?: string | string[]; // from mongo
  customerId?: string;
  customerEmail?: string;
  customerFirstName?: string;
  customerLastName?: string;
  customerName?: string;
  minCreatedDate?: string | Date;
  maxCreatedDate?: string | Date;
  minUpdatedDate?: string | Date;
  maxUpdatedDate?: string | Date;
  minOrderId?: number;
  maxOrderId?: number;
  limit?: number;
  offset?: number;
  orderId?: number;
  includeArchived?: boolean;
  sortOrder?: 1 | -1;
  sortField?: string;
  product?: string;
  productCategory?: string;
  productIds?: string[];
  includeCount?: boolean;
  source?: OrderSource | OrderSource[];
  subscriptionId?: string;
  cartId?: string;
  pharmetikaOrderId?: string;
  curexaId?: string;
  belugaNeedsAttention?: boolean;
  pharmacyId?: string | string[];
  paymentStatus?: string | string[];
  meta?: TMeta;
  extras?: TExtras;
  coupon?: string;
}

export interface FindAllConfig {
  maxLimit: number;
}

export class OrderShipment {
  carrier?: string;
  service?: string;
  cost?: number;
  trackingCode?: string;
  trackingLink?: string;
  items?: {
    name: string;
    quantity: number;
  }[];
}

export class IOrderStatsQuery {
  dateStart?: Date | string;
  dateEnd?: Date | string;
  productCategories?: string[] | string;
  productType?: 'all' | 'digital' | 'physical';
}

export class ICreateChargeResponse {
  id: string;
  response: string;
  status: string;
  amount?: number;
}

export class IOrderPayment {
  status: PaymentStatusKey;
  paymentId: string;
  gatewayOrderId?: string;
  /** Processor transaction identifier persisted for processor-neutral follow-ups. */
  processorPaymentId?: string;
  /** Order identifier sent to the processor. */
  processorOrderId?: string;
  /** Customer/vault identifier sent to the processor. */
  processorCustomerId?: string;
  /** Saved payment method used for later processor charges, when available. */
  paymentMethodId?: string;
  refundId?: string;
  refunds?: IOrderPaymentRefund[];
  amount?: number;
  paidAt?: Date;
  /** The vendor holding the money. Never `legacy` — that is a routing mode. */
  gateway: PaymentProcessorId;
  /**
   * The concrete code path that ran, which `gateway` cannot express: modern
   * Stripe and the frozen legacy Stripe path are both `gateway: 'stripe'` and
   * produce the same `pi_…` ids, so this is the only way to tell them apart
   * after the fact.
   */
  processor?: PaymentProcessorImplementationId;
  failedReason?: string;
  refundAmount?: number;
  delayedAmount?: number;
  refundedCreditAmount?: number;
  returnedPostPurchaseCredit?: number;
  refundedBy?: string | Types.ObjectId;
  failedAttemptCount?: number; //Tracks retry attempts when payment status is failed.
}

export class IOrderPaymentRefund {
  /** Unique identifier for this local refund record. */
  id: string;
  /** Identifier returned by the gateway when the refund was submitted. */
  gatewayRefundId?: string;
  /** Stable identifier for an action returned by the gateway query API. */
  gatewayActionId?: string;
  /** The vendor that held the money. Never `legacy`. */
  gateway: PaymentProcessorId;
  /**
   * Concrete code path that issued the refund. Distinguishes modern Stripe from
   * `legacy-stripe` the same way `IOrderPayment.processor` does.
   */
  processor?: PaymentProcessorImplementationId;
  /** Refunded amount in dollars, consistent with IOrderPayment.refundAmount. */
  amount: number;
  /** Total refund represented by this operation, including customer credit. */
  requestedAmount?: number;
  creditAmount?: number;
  status: 'succeeded' | 'pending' | 'failed' | 'canceled' | 'requires_action';
  reason?: 'duplicate' | 'fraudulent' | 'requested_by_customer';
  createdAt: Date;
  gatewayAttemptedAt?: Date;
  creditAppliedAt?: Date;
  completedAt?: Date;
  failedReason?: string;
  refundedBy?: string | Types.ObjectId;
}

export interface IRefundProduct {
  productId: string;
  quantity: number;
  refundAmount: number;
}

export interface IOrderRefundData {
  amount?: number;
  reason?: 'duplicate' | 'fraudulent' | 'requested_by_customer';
  refundedProducts?: IRefundProduct[];
}

export interface IOrderRefundResult {
  success: boolean;
  refundId: string;
  refundAmount: number;
  creditRefundAmount?: number;
  paymentMethodRefundAmount?: number;
  newPaymentStatus: PaymentStatusKey;
  totalRefundAmount: number;
  order: IOrder;
}

export interface IPocketPillsOrderItem {
  id: number;
  name: string;
  quantity: number;
  dosageType: string;
  governmentAmount: string;
  insuranceAmount: string;
  coPayAmount: string;
  totalAmount: string;
}

export type PharmacyOrderProvider = 'pocketpills' | string;

export interface IPocketPillsPharmacyOrderSnapshot {
  orderItems?: IPocketPillsOrderItem[];
  originalCardCharged?: number;
  originalCreditUsed?: number;
  processedEventIds?: string[];
  /**
   * Idempotency keys for post-checkout credit draws (order + copay).
   * Guards creditUsedAmount so HTTP charge and ORDER_ADJUDICATED cannot
   * increment the same shortfall twice.
   */
  adjudicationCreditDrawKeys?: string[];
  adjudicationHistory?: {
    eventId?: string;
    timestamp: number;
    ppCoPay: number;
    ppInsurance: number;
    computedTargetNet: number;
    refundIssued: number;
  }[];
  [key: string]: unknown;
}

export interface IPharmacyOrder {
  [provider: PharmacyOrderProvider]: IPocketPillsPharmacyOrderSnapshot;
}
